import { Check } from 'lucide-react';
import { useEffect, useState } from 'react';
import { fetchWithAuth } from '../Utils/fetchWithAuth';
import ModalWrapper from './UI/ModalWrapper';
import { API_ENDPOINTS } from '../Config/api';
import usePost from '../Hooks/usePost';
import DOMPurify from 'dompurify';

export default function LegalComplianceEnforcer() {
    const [legalDocs, setLegalDocs] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [acceptedIds, setAcceptedIds] = useState([]);
    const [loadingId, setLoadingId] = useState(null);

    const { postData } = usePost();

    useEffect(() => {
        const fetchLegal = async () => {
            try {
                const res = await fetchWithAuth(API_ENDPOINTS.pendingDocuments);
                const data = await res.json();

                const obbligatori = data.documenti.filter(
                    (doc) => doc.tipo_documento.obbligatorio && !doc.accettato
                );

                if (obbligatori.length > 0) {
                    setLegalDocs(obbligatori);
                    setShowModal(true);
                } else {
                    // Nessun documento pendente: sblocca il tour guidato
                    window.dispatchEvent(new CustomEvent('legal-docs-cleared'));
                }
            } catch (err) {
                console.error('Errore controllo legal compliance:', err);
                // In caso di errore, non bloccare il tour
                window.dispatchEvent(new CustomEvent('legal-docs-cleared'));
            }
        };

        fetchLegal();
        const interval = setInterval(fetchLegal, 5 * 60 * 1000);
        return () => clearInterval(interval);
    }, []);

    const handleAccept = async (id) => {
        try {
            setLoadingId(id);
            await postData(API_ENDPOINTS.acceptDocument, { documento: id });
            setAcceptedIds((prev) => [...prev, id]);
            setLegalDocs((prev) => prev.filter((doc) => doc.id_documento !== id));
            if (legalDocs.length === 1) {
                setShowModal(false);
                // Tutti i documenti accettati: sblocca il tour guidato
                window.dispatchEvent(new CustomEvent('legal-docs-cleared'));
            }
        } catch (err) {
            console.error('Errore accettazione documento:', err);
        } finally {
            setLoadingId(null);
        }
    };

    return (
        <ModalWrapper isOpen={showModal} position="center" onClose={null} closeOnBackdrop={false}>
            <div className="p-6 bg-white rounded-lg max-h-[80vh] overflow-auto space-y-6 w-[95vw] md:w-[600px] max-w-2xl">
                <h2 className="text-xl font-semibold">Accettazione Documenti Legali</h2>
                {legalDocs.map((doc) => {
                    const isAccepted = acceptedIds.includes(doc.id_documento);
                    const isLoading = loadingId === doc.id_documento;

                    return (
                        <div key={doc.id_documento} className="border rounded p-4 bg-gray-50 space-y-2">
                            <div className="flex justify-between">
                                <span className="font-medium">{doc.tipo_documento.nome}</span>
                                <span className="text-sm text-gray-500">v{doc.versione}</span>
                            </div>
                            <div
                                className="prose text-sm text-gray-700 max-w-full"
                                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(doc.contenuto_html) }}
                            />
                            {isAccepted ? (
                                <p className="text-green-600 text-sm font-medium"><Check size={14} className="inline" /> Documento accettato</p>
                            ) : (
                                <button
                                    onClick={() => handleAccept(doc.id_documento)}
                                    disabled={isLoading}
                                    className="bg-blue-600 text-white px-4 py-2 rounded text-sm disabled:opacity-50"
                                >
                                    {isLoading ? 'Invio...' : 'Accetta'}
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>
        </ModalWrapper>
    );
}
